import React from 'react';
import PropTypes from 'prop-types';
import { Howler } from 'howler';

import { createSounds as createSoundsModule } from '../../tools/createSounds';
import { createPlayer as createPlayerModule } from '../../tools/createPlayer';
import { SoundsContext } from '../SoundsContext';

class Component extends React.PureComponent {
  static propTypes = {
    sounds: PropTypes.object.isRequired,
    createPlayer: PropTypes.func.isRequired,
    createSounds: PropTypes.func.isRequired,
    children: PropTypes.any
  };

  static defaultProps = {
    createPlayer: createPlayerModule,
    createSounds: createSoundsModule
  };

  constructor () {
    super(...arguments);

    const sounds = this.getSounds();

    this.state = { sounds };
    this.audioUnlocked = typeof window === 'undefined';
  }

  componentDidMount () {
    this.attachUnlockListener();
  }

  componentDidUpdate (prevProps) {
    if (prevProps.sounds !== this.props.sounds) {
      const sounds = this.getSounds();
      this.setState({ sounds }); // eslint-disable-line react/no-did-update-set-state
    }
  }

  componentWillUnmount () {
    this.detachUnlockListener();
  }

  attachUnlockListener () {
    if (this.audioUnlocked || typeof window === 'undefined') return;

    this.unlockHandler = () => {
      this.tryUnlockAudioContext();
    };
    window.addEventListener('pointerdown', this.unlockHandler, { once: true });
    window.addEventListener('keydown', this.unlockHandler, { once: true });
    window.addEventListener('touchstart', this.unlockHandler, { once: true, passive: true });
  }

  detachUnlockListener () {
    if (!this.unlockHandler || typeof window === 'undefined') return;
    window.removeEventListener('pointerdown', this.unlockHandler);
    window.removeEventListener('keydown', this.unlockHandler);
    window.removeEventListener('touchstart', this.unlockHandler);
    this.unlockHandler = null;
  }

  tryUnlockAudioContext () {
    if (this.audioUnlocked || typeof Howler === 'undefined' || !Howler.ctx) {
      this.audioUnlocked = true;
      return;
    }

    const attemptResume = () => {
      try {
        const ctx = Howler.ctx;
        if (ctx && typeof ctx.resume === 'function' && ctx.state === 'suspended') {
          ctx.resume()
            .catch(() => {})
            .finally(() => {
              this.audioUnlocked = ctx.state === 'running';
            });
        } else {
          this.audioUnlocked = true;
        }
      } catch (e) {
        this.audioUnlocked = true;
      } finally {
        this.detachUnlockListener();
      }
    };

    // Some browsers require `resume` to happen in the same tick as the event.
    requestAnimationFrame(attemptResume);
  }

  getSounds () {
    const { sounds, createPlayer, createSounds } = this.props;
    const { settings, players } = createSounds(sounds);

    const soundsPlayers = {};

    Object.keys(players).forEach(name => {
      const player = players[name];
      soundsPlayers[name] = createPlayer({ ...settings, ...player });
    });

    return soundsPlayers;
  }

  render () {
    const { sounds } = this.state;
    const { children } = this.props;

    return (
      <SoundsContext.Provider value={sounds}>
        {children}
      </SoundsContext.Provider>
    );
  }
}

export { Component };
